import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { childrenAPI, lessonsAPI, roomsAPI, learningPathsAPI, recessAPI, parentSharesAPI, avatarAPI, billingAPI } from '../api';
import api from '../api';
import { toast } from 'sonner';
import { Plus, BookOpen, Users, LogOut, Edit, Trash2, Eye, Route, Sparkles, TrendingUp, Settings, Clock, Star, Check, X, Share2, Wand2, User, BarChart3, PenTool, Mic, Award, Calendar, ChevronDown, ChevronUp, Flame, Crown, Zap } from 'lucide-react';
import { AvatarPreview, AvatarBuilderModal } from '../components/AvatarBuilder';
import { FeedbackButton } from '../components/FeedbackButton';

// Recess Settings Panel Component
const RecessSettingsPanel = ({ children }) => {
  const [selectedChild, setSelectedChild] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const ROOM_OPTIONS = [
    { id: 'story_circle', name: 'Story Circle', desc: 'Collaborative storytelling' },
    { id: 'question_garden', name: 'Question Garden', desc: 'Ask & answer questions' },
    { id: 'celebration_hall', name: 'Celebration Hall', desc: 'Share achievements' }
  ];

  const loadSettings = async (childId) => {
    setLoading(true);
    try {
      const res = await recessAPI.getSettings(childId);
      setSettings(res.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      // Set defaults if no settings exist
      setSettings({
        enabled: false,
        allowed_rooms: [],
        time_limit_minutes: 15,
        allow_preset_phrases: true,
        allow_emojis: true,
        allow_story_continuation: true,
        allow_questions: true
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!selectedChild || !settings) return;
    
    setSaving(true);
    try {
      await recessAPI.updateSettings(selectedChild.id, {
        child_id: selectedChild.id,
        ...settings
      });
      toast.success('Recess settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectChild = (child) => {
    setSelectedChild(child);
    loadSettings(child.id);
  };

  const toggleRoom = (roomId) => {
    const current = settings.allowed_rooms || [];
    if (current.includes(roomId)) {
      setSettings({ ...settings, allowed_rooms: current.filter(r => r !== roomId) });
    } else {
      setSettings({ ...settings, allowed_rooms: [...current, roomId] });
    }
  };

  return (
    <div className="glass-panel p-6 rounded-2xl mb-8">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-purple-400" />
        <h2 className="text-2xl font-philosopher font-bold text-white">Recess Settings</h2>
      </div>
      
      <p className="text-slate-400 text-sm mb-4">
        Control your children's access to Recess Portal rooms. Recess unlocks when daily quests are completed.
      </p>

      {/* Child selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {children.map(child => (
          <button
            key={child.id}
            onClick={() => handleSelectChild(child)}
            className={`px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base truncate max-w-[150px] sm:max-w-none ${
              selectedChild?.id === child.id
                ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50'
            }`}
          >
            {child.name}
          </button>
        ))}
      </div>

      {selectedChild && settings && !loading && (
        <div className="space-y-6">
          {/* Enable toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
            <div>
              <p className="text-white font-semibold">Enable Recess Portal</p>
              <p className="text-slate-500 text-xs mt-1">
                {settings.enabled ? 'Child can access recess after completing daily quests' : 'Recess is disabled for this child'}
              </p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
              className={`w-14 h-7 rounded-full transition-all relative ${
                settings.enabled ? 'bg-purple-500' : 'bg-slate-700'
              }`}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                settings.enabled ? 'left-8' : 'left-1'
              }`} />
            </button>
          </div>

          {settings.enabled && (
            <>
              {/* Time limit */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <p className="text-white font-semibold">Time Limit</p>
                </div>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="5"
                    max="30"
                    value={settings.time_limit_minutes}
                    onChange={(e) => setSettings({ ...settings, time_limit_minutes: parseInt(e.target.value) })}
                    className="flex-1 accent-purple-500"
                  />
                  <span className="text-white font-semibold w-20">
                    {settings.time_limit_minutes} min
                  </span>
                </div>
              </div>

              {/* Allowed rooms */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <p className="text-white font-semibold mb-3">Allowed Rooms</p>
                <div className="space-y-2">
                  {ROOM_OPTIONS.map(room => (
                    <button
                      key={room.id}
                      onClick={() => toggleRoom(room.id)}
                      className={`w-full p-3 rounded-lg text-left transition-all ${
                        (settings.allowed_rooms || []).includes(room.id)
                          ? 'bg-purple-500/20 border border-purple-500/50 text-purple-300'
                          : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50'
                      }`}
                    >
                      <p className="font-semibold">{room.name}</p>
                      <p className="text-xs opacity-70">{room.desc}</p>
                    </button>
                  ))}
                </div>
                <p className="text-slate-600 text-xs mt-2">
                  {(settings.allowed_rooms || []).length === 0 && 'Select at least one room to enable recess'}
                </p>
              </div>

              {/* Communication options */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <p className="text-white font-semibold mb-3">Communication Options</p>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allow_preset_phrases}
                      onChange={(e) => setSettings({ ...settings, allow_preset_phrases: e.target.checked })}
                      className="w-4 h-4 rounded accent-purple-500"
                    />
                    <span className="text-slate-300 text-sm">Allow preset phrases</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allow_emojis}
                      onChange={(e) => setSettings({ ...settings, allow_emojis: e.target.checked })}
                      className="w-4 h-4 rounded accent-purple-500"
                    />
                    <span className="text-slate-300 text-sm">Allow emoji reactions</span>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Save button */}
          <button
            onClick={saveSettings}
            disabled={saving}
            className="w-full py-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-xl transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-white/20 border-t-purple-500 rounded-full mx-auto"></div>
        </div>
      )}

      {!selectedChild && (
        <p className="text-slate-500 text-center py-4">Select a child to configure their recess settings</p>
      )}
    </div>
  );
};

// Pending Community Shares Panel Component
const PendingSharesPanel = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    loadPending();
  }, []);

  const loadPending = async () => {
    try {
      const res = await parentSharesAPI.getPending();
      setPending(res.data.pending_shares || []);
    } catch (error) {
      console.error('Error loading pending shares:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (constellationId, name) => {
    setProcessing(constellationId);
    try {
      await parentSharesAPI.approve(constellationId);
      toast.success(`"${name}" is now shared in the Temple Sky!`);
      setPending(prev => prev.filter(p => p.id !== constellationId));
    } catch (error) {
      toast.error('Could not approve share');
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async (constellationId, name) => {
    setProcessing(constellationId);
    try {
      await parentSharesAPI.decline(constellationId);
      toast.info(`"${name}" will stay in Family Sky only`);
      setPending(prev => prev.filter(p => p.id !== constellationId));
    } catch (error) {
      toast.error('Could not decline share');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="glass-panel p-6 rounded-2xl mb-8">
        <div className="text-center py-4">
          <div className="animate-spin w-6 h-6 border-2 border-white/20 border-t-amber-500 rounded-full mx-auto"></div>
        </div>
      </div>
    );
  }

  if (pending.length === 0) {
    return null; // Don't show panel if no pending shares
  }

  return (
    <div className="glass-panel p-6 rounded-2xl mb-8 border border-amber-500/30" data-testid="pending-shares-panel">
      <div className="flex items-center gap-3 mb-4">
        <Share2 className="w-6 h-6 text-amber-400" />
        <div>
          <h2 className="text-xl font-philosopher font-bold text-white">Pending Community Shares</h2>
          <p className="text-sm text-slate-400">
            Your children want to share these constellations with the Temple community
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {pending.map(constellation => (
          <div 
            key={constellation.id}
            className="bg-slate-900/50 p-4 rounded-xl flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="text-white font-semibold">{constellation.name}</p>
                <p className="text-sm text-slate-400">
                  By {constellation.child_name} • {constellation.stars?.length || 0} stars
                </p>
                {constellation.story && (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">"{constellation.story}"</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDecline(constellation.id, constellation.name)}
                disabled={processing === constellation.id}
                className="p-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 rounded-lg transition-all disabled:opacity-50"
                title="Keep in Family Sky only"
              >
                <X className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleApprove(constellation.id, constellation.name)}
                disabled={processing === constellation.id}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg transition-all disabled:opacity-50"
              >
                {processing === constellation.id ? (
                  <div className="animate-spin w-4 h-4 border-2 border-amber-300/20 border-t-amber-300 rounded-full"></div>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Approve</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-500 mt-4">
        Approved constellations will be shown anonymously as "Created by a Young Explorer – Age X"
      </p>
    </div>
  );
};

// ============ TEMPLE LISTENS READING PROGRESS PANEL ============
const ReadingProgressPanel = ({ children }) => {
  const [selectedChild, setSelectedChild] = useState(null);
  const [readingData, setReadingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    sessions: false,
    words: false
  });

  const loadReadingProgress = async (childId) => {
    setLoading(true);
    try {
      const res = await api.get(`/temple-listens/progress/${childId}`);
      setReadingData(res.data);
    } catch (error) {
      console.error('Error loading reading progress:', error);
      setReadingData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChild = (child) => {
    setSelectedChild(child);
    loadReadingProgress(child.id);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Unknown';
    }
  };

  // Level colors
  const levelColors = {
    advanced: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
    intermediate: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    beginner: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
    early: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' }
  };

  return (
    <div className="glass-panel p-6 rounded-2xl mb-8" data-testid="reading-progress-panel">
      <div className="flex items-center gap-3 mb-6">
        <Mic className="w-6 h-6 text-purple-400" />
        <h2 className="text-2xl font-philosopher font-bold text-white">Temple Listens - Reading Progress</h2>
      </div>

      {/* Child Selection */}
      <div className="flex flex-wrap gap-2 mb-6">
        {children.map(child => (
          <button
            key={child.id}
            onClick={() => handleSelectChild(child)}
            className={`px-4 py-2 rounded-lg transition-all ${
              selectedChild?.id === child.id
                ? 'bg-purple-500 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            data-testid={`reading-child-${child.id}`}
          >
            {child.name}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-slate-400">Loading reading progress...</p>
        </div>
      )}

      {/* No Data State */}
      {!loading && selectedChild && !readingData?.summary?.total_readings && (
        <div className="text-center py-8 bg-slate-800/50 rounded-xl">
          <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 mb-2">{selectedChild.name} hasn't started reading with Temple Listens yet.</p>
          <p className="text-slate-500 text-sm">Reading sessions will appear here once they begin.</p>
        </div>
      )}

      {/* Reading Progress Data */}
      {!loading && readingData?.summary?.total_readings > 0 && (
        <div className="space-y-6">
          
          {/* ===== QUICK SUMMARY ===== */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {/* Total Readings */}
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <BookOpen className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{readingData.summary.total_readings}</p>
              <p className="text-xs text-slate-400">Stories Read</p>
            </div>
            
            {/* Average Accuracy */}
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <Award className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{readingData.summary.average_accuracy}%</p>
              <p className="text-xs text-slate-400">Avg Accuracy</p>
            </div>
            
            {/* Reading Streak */}
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <Flame className="w-6 h-6 text-orange-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{readingData.summary.reading_streak}</p>
              <p className="text-xs text-slate-400">Day Streak</p>
            </div>
            
            {/* Current Level */}
            <div className={`rounded-xl p-4 text-center ${levelColors[readingData.summary.current_level]?.bg || 'bg-slate-800/50'}`}>
              <Star className={`w-6 h-6 mx-auto mb-2 ${levelColors[readingData.summary.current_level]?.text || 'text-slate-400'}`} />
              <p className={`text-lg font-bold ${levelColors[readingData.summary.current_level]?.text || 'text-white'}`}>
                {readingData.summary.level_label}
              </p>
              <p className="text-xs text-slate-400">Reading Level</p>
            </div>
          </div>

          {/* Last Session & Trend */}
          <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-800/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-400">Last Reading Session</p>
                <p className="text-white font-medium">{formatDate(readingData.summary.last_session)}</p>
              </div>
            </div>
            
            {readingData.summary.accuracy_trend !== 0 && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                readingData.summary.accuracy_trend > 0 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                <TrendingUp className={`w-4 h-4 ${readingData.summary.accuracy_trend < 0 ? 'rotate-180' : ''}`} />
                <span className="text-sm font-medium">
                  {readingData.summary.accuracy_trend > 0 ? '+' : ''}{readingData.summary.accuracy_trend}% this week
                </span>
              </div>
            )}
            
            {readingData.summary.total_time_minutes > 0 && (
              <div className="flex items-center gap-2 text-slate-400">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{readingData.summary.total_time_minutes} min total reading</span>
              </div>
            )}
          </div>

          {/* ===== ACCURACY TREND CHART ===== */}
          {readingData.trend_data?.length > 2 && (
            <div className="bg-slate-800/30 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Reading Progress Over Time</h3>
              <div className="h-24 flex items-end gap-1">
                {readingData.trend_data.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-purple-500/50 rounded-t transition-all hover:bg-purple-400/50"
                      style={{ height: `${Math.max(day.accuracy, 10)}%` }}
                      title={`${day.date}: ${day.accuracy}% accuracy, ${day.readings} readings`}
                    />
                    <span className="text-[10px] text-slate-500 mt-1 truncate w-full text-center">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== WORDS TO PRACTICE ===== */}
          {readingData.words_struggled?.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <button 
                onClick={() => toggleSection('words')}
                className="w-full flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-amber-300 flex items-center gap-2">
                  <PenTool className="w-4 h-4" />
                  Words to Practice Together ({readingData.words_struggled.length})
                </h3>
                {expandedSections.words ? <ChevronUp className="w-4 h-4 text-amber-400" /> : <ChevronDown className="w-4 h-4 text-amber-400" />}
              </button>
              
              {expandedSections.words && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {readingData.words_struggled.map((item, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-amber-500/20 text-amber-200 rounded-full text-sm"
                    >
                      {item.word} <span className="text-amber-400 text-xs">×{item.count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== RECENT SESSIONS ===== */}
          {readingData.recent_sessions?.length > 0 && (
            <div className="bg-slate-800/30 rounded-xl p-4">
              <button 
                onClick={() => toggleSection('sessions')}
                className="w-full flex items-center justify-between mb-3"
              >
                <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Recent Reading Sessions
                </h3>
                {expandedSections.sessions ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              
              {/* Always show last 3, expandable for more */}
              <div className="space-y-2">
                {readingData.recent_sessions.slice(0, expandedSections.sessions ? 10 : 3).map((session, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      session.passed ? 'bg-emerald-500/10' : 'bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {session.passed ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <X className="w-4 h-4 text-slate-500" />
                      )}
                      <div>
                        <p className="text-white text-sm font-medium">{session.passage_title}</p>
                        <p className="text-xs text-slate-400">
                          {session.category} • {session.difficulty}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${
                        session.accuracy >= 70 ? 'text-emerald-400' : 'text-amber-400'
                      }`}>
                        {session.accuracy}%
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDate(session.completed_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {readingData.recent_sessions.length > 3 && !expandedSections.sessions && (
                <button 
                  onClick={() => toggleSection('sessions')}
                  className="w-full mt-3 text-sm text-purple-400 hover:text-purple-300"
                >
                  Show {readingData.recent_sessions.length - 3} more sessions
                </button>
              )}
            </div>
          )}

          {/* ===== CATEGORY BREAKDOWN ===== */}
          {readingData.category_breakdown && Object.keys(readingData.category_breakdown).length > 0 && (
            <div className="bg-slate-800/30 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Reading by Category</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(readingData.category_breakdown).map(([category, stats]) => (
                  <div key={category} className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <p className="text-white font-medium capitalize">{category.replace('_', ' ')}</p>
                    <p className="text-lg font-bold text-purple-400">{stats.avg_accuracy}%</p>
                    <p className="text-xs text-slate-400">{stats.count} readings</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prompt to select a child */}
      {!selectedChild && (
        <p className="text-slate-400 text-center py-4">
          Select a child above to view their reading progress
        </p>
      )}
    </div>
  );
};

const ParentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Parent avatar state
  const [parentAvatar, setParentAvatar] = useState(null);
  const [showAvatarBuilder, setShowAvatarBuilder] = useState(false);
  
  // Child selection modal state for Journeys
  const [showChildSelectModal, setShowChildSelectModal] = useState(false);
  
  // Drill-down detail modals state
  const [showLessonsDetail, setShowLessonsDetail] = useState(false);
  const [showSparksDetail, setShowSparksDetail] = useState(false);
  const [showActivityDetail, setShowActivityDetail] = useState(false);
  const [showJourneysDetail, setShowJourneysDetail] = useState(false);
  const [detailChild, setDetailChild] = useState(null); // Which child's detail to show
  const [childProgress, setChildProgress] = useState({}); // Progress data per child
  
  // Billing status state
  const [billingStatus, setBillingStatus] = useState(null);

  useEffect(() => {
    fetchData();
    fetchParentAvatar();
    fetchBillingStatus();
  }, []);

  // Fetch progress for all children when children list changes
  useEffect(() => {
    const fetchAllProgress = async () => {
      const progressData = {};
      for (const child of children) {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/children/${child.id}/progress`);
          if (res.ok) {
            const data = await res.json();
            progressData[child.id] = data;
          }
        } catch (error) {
          console.error(`Error fetching progress for ${child.name}:`, error);
        }
      }
      setChildProgress(progressData);
    };
    
    if (children.length > 0) {
      fetchAllProgress();
    }
  }, [children]);

  const fetchParentAvatar = async () => {
    try {
      const res = await avatarAPI.getParentAvatar();
      setParentAvatar(res.data?.avatar || null);
    } catch (error) {
      console.error('Error fetching parent avatar:', error);
    }
  };
  
  const fetchBillingStatus = async () => {
    try {
      const res = await billingAPI.getSubscriptionStatus();
      setBillingStatus(res.data);
    } catch (error) {
      console.error('Error fetching billing status:', error);
    }
  };

  const handleAvatarSave = (newAvatar) => {
    setParentAvatar(newAvatar);
    setShowAvatarBuilder(false);
  };

  const fetchData = async () => {
    try {
      const [childrenRes, lessonsRes, roomsRes, pathsRes] = await Promise.all([
        childrenAPI.getAll(),
        lessonsAPI.getAll(),
        roomsAPI.getAll(),
        learningPathsAPI.getAll(),
      ]);
      setChildren(childrenRes.data);
      setLessons(lessonsRes.data);
      setRooms(roomsRes.data);
      setPaths(pathsRes.data || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (window.confirm('Are you sure you want to delete this lesson?')) {
      try {
        await lessonsAPI.delete(lessonId);
        toast.success('Lesson deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete lesson');
      }
    }
  };

  const handleCreateSampleLessons = async () => {
    try {
      const sampleLessons = [
        {
          title: "Ancient Greek Mythology",
          description: "Discover the gods and heroes of ancient Greece",
          room_id: "memory_garden",
          blocks: [
            { type: "text", content: "Greek mythology is filled with powerful gods, brave heroes, and exciting adventures. Let us explore!", order: 0 },
            { type: "question", content: "Who was the king of the Greek gods?", order: 1, options: ["Zeus", "Poseidon", "Hades", "Apollo"], correct_answer: "Zeus" }
          ],
          is_published: true
        },
        {
          title: "Introduction to Space",
          description: "Learn about stars, planets, and the universe",
          room_id: "star_map",
          blocks: [
            { type: "text", content: "Welcome to the wonderful world of space! In this lesson, you will learn about stars, planets, and the vast universe.", order: 0 },
            { type: "image", content: "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800", order: 1 },
            { type: "question", content: "What is the closest star to Earth?", order: 2, options: ["The Moon", "The Sun", "Mars", "Jupiter"], correct_answer: "The Sun" }
          ],
          is_published: true
        },
        {
          title: "Mindful Breathing",
          description: "Learn to calm your mind through breathing exercises",
          room_id: "calm_cove",
          blocks: [
            { type: "text", content: "Breathing exercises help us feel calm and peaceful. Let us practice together.", order: 0 },
            { type: "text", content: "Close your eyes. Breathe in slowly for 4 counts. Hold for 4 counts. Breathe out for 4 counts. Repeat 3 times.", order: 1 }
          ],
          is_published: true
        },
        {
          title: "Colors and Emotions",
          description: "Explore how colors make us feel",
          room_id: "creative_forge",
          blocks: [
            { type: "text", content: "Colors can make us feel different emotions. Red is exciting, blue is calm, yellow is happy!", order: 0 },
            { type: "question", content: "Which color makes you feel calm?", order: 1, options: ["Red", "Blue", "Yellow", "Orange"], correct_answer: "Blue" }
          ],
          is_published: true
        },
        {
          title: "Building Confidence",
          description: "Learn to believe in yourself",
          room_id: "radiant_will",
          blocks: [
            { type: "text", content: "Confidence means believing in yourself. Everyone has special talents and abilities!", order: 0 },
            { type: "question", content: "What should you do when you make a mistake?", order: 1, options: ["Give up", "Try again", "Feel bad forever", "Hide"], correct_answer: "Try again" }
          ],
          is_published: true
        }
      ];

      // Create all sample lessons
      for (const lessonData of sampleLessons) {
        await lessonsAPI.create(lessonData);
      }

      toast.success(`Created ${sampleLessons.length} sample lessons!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to create sample lessons');
      console.error('Error creating sample lessons:', error);
    }
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : roomId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl font-philosopher">Loading...</div>
      </div>
    );
  }

  // Helper: Calculate total sparks across all children
  const getTotalSparks = () => {
    return children.reduce((total, child) => {
      // Use total_sparks from backend (pre-calculated), fallback to progress or 0
      const childSparks = child.total_sparks ?? 0;
      const progressSparks = childProgress[child.id]?.total_sparks ?? 0;
      return total + Math.max(childSparks, progressSparks);
    }, 0);
  };
  
  // Helper: Get per-child spark breakdown for display
  const getChildrenSparks = () => {
    return children.map(child => ({
      id: child.id,
      name: child.name,
      sparks: child.total_sparks ?? childProgress[child.id]?.total_sparks ?? 0
    }));
  };

  // Helper: Get all completed lessons across children
  const getCompletedLessons = () => {
    const completed = [];
    children.forEach(child => {
      const progress = childProgress[child.id];
      if (progress?.completed_lessons) {
        progress.completed_lessons.forEach(lesson => {
          completed.push({ ...lesson, childName: child.name, childId: child.id });
        });
      }
    });
    return completed.sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
  };

  // Drill-Down Modal: Lessons Completed
  const LessonsDetailModal = () => (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-panel p-6 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-400" />
            Lessons Completed
          </h3>
          <button onClick={() => setShowLessonsDetail(false)} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="overflow-y-auto flex-1 space-y-3">
          {children.map(child => {
            const progress = childProgress[child.id];
            const completedLessons = progress?.completed_lessons || [];
            
            return (
              <div key={child.id} className="bg-slate-900/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-indigo-400" />
                  </div>
                  <span className="text-white font-semibold">{child.name}</span>
                  <span className="text-slate-500 text-sm">({completedLessons.length} lessons)</span>
                </div>
                
                {completedLessons.length > 0 ? (
                  <div className="space-y-2">
                    {completedLessons.slice(0, 5).map((lesson, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-slate-800/50 rounded-lg px-3 py-2">
                        <span className="text-slate-300">{lesson.title || lesson.lesson_id || 'Lesson'}</span>
                        <div className="flex items-center gap-2">
                          <Star className="w-3 h-3 text-amber-400" />
                          <span className="text-amber-400">{lesson.sparks_earned || 10}</span>
                        </div>
                      </div>
                    ))}
                    {completedLessons.length > 5 && (
                      <p className="text-slate-500 text-xs text-center">+ {completedLessons.length - 5} more lessons</p>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No lessons completed yet</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Drill-Down Modal: Sparks Earned
  const SparksDetailModal = () => (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-panel p-6 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Sparks Earned
          </h3>
          <button onClick={() => setShowSparksDetail(false)} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center">
          <p className="text-amber-400 text-3xl font-bold">{getTotalSparks()}</p>
          <p className="text-slate-400 text-sm">Total Family Sparks</p>
        </div>
        
        <div className="overflow-y-auto flex-1 space-y-3">
          {children.map(child => {
            const progress = childProgress[child.id];
            const sparks = progress?.sparks || child.sparks || 0;
            const streak = progress?.streak || 0;
            
            return (
              <div key={child.id} className="bg-slate-900/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-amber-400" />
                    </div>
                    <span className="text-white font-semibold">{child.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-400 font-bold">{sparks}</span>
                    </div>
                    {streak > 0 && (
                      <div className="flex items-center gap-1 text-orange-400">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm">{streak} day streak</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Spark sources breakdown */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                    <p className="text-slate-400">Lessons</p>
                    <p className="text-emerald-400 font-semibold">{progress?.lessons_completed || 0} × 10</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                    <p className="text-slate-400">Activities</p>
                    <p className="text-blue-400 font-semibold">{progress?.activities_completed || 0} × 5</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                    <p className="text-slate-400">Streak Bonus</p>
                    <p className="text-orange-400 font-semibold">+{streak * 2}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Drill-Down Modal: Journey Progress
  const JourneysDetailModal = () => (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-panel p-6 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
            <Route className="w-5 h-5 text-purple-400" />
            Journey Progress
          </h3>
          <button onClick={() => setShowJourneysDetail(false)} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="overflow-y-auto flex-1 space-y-4">
          {children.map(child => {
            const progress = childProgress[child.id];
            const journeyProgress = progress?.journey_progress || {};
            
            return (
              <div key={child.id} className="bg-slate-900/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-purple-400" />
                  </div>
                  <span className="text-white font-semibold">{child.name}</span>
                </div>
                
                {paths.length > 0 ? (
                  <div className="space-y-3">
                    {paths.map(path => {
                      const pathProgress = journeyProgress[path.id] || { completed: 0, total: path.lesson_ids?.length || 5 };
                      const percent = pathProgress.total > 0 ? (pathProgress.completed / pathProgress.total) * 100 : 0;
                      
                      return (
                        <div key={path.id} className="bg-slate-800/50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-slate-300 text-sm">{path.name}</span>
                            <span className="text-slate-500 text-xs">{pathProgress.completed}/{pathProgress.total} lessons</span>
                          </div>
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No journeys available yet</p>
                )}
              </div>
            );
          })}
        </div>
        
        <button
          onClick={() => { setShowJourneysDetail(false); navigate('/journey-selection'); }}
          className="mt-4 w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl px-4 py-3 font-semibold hover:opacity-90 transition-all"
        >
          Explore Journeys
        </button>
      </div>
    </div>
  );

  // Drill-Down Modal: Activity History
  const ActivityDetailModal = () => {
    // Get recent activity from childProgress
    const getRecentActivity = () => {
      const activities = [];
      
      children.forEach(child => {
        const progress = childProgress[child.id];
        if (!progress) return;
        
        // Add completed lessons
        if (progress.completed_lessons) {
          progress.completed_lessons.forEach(lessonId => {
            const lesson = lessons.find(l => l.id === lessonId);
            activities.push({
              childId: child.id,
              childName: child.name,
              type: 'lesson_complete',
              title: lesson?.title || 'Lesson',
              date: progress.last_activity || new Date().toISOString(),
              icon: BookOpen,
              color: 'green'
            });
          });
        }
        
        // Add sparks earned
        if (progress.sparks > 0) {
          activities.push({
            childId: child.id,
            childName: child.name,
            type: 'sparks_earned',
            title: `Earned ${progress.sparks} sparks`,
            date: progress.last_activity || new Date().toISOString(),
            icon: Sparkles,
            color: 'amber'
          });
        }
        
        // Add journey progress
        if (progress.journey_progress) {
          Object.entries(progress.journey_progress).forEach(([journeyId, jp]) => {
            if (jp.completed > 0) {
              const journey = paths.find(p => p.id === journeyId);
              activities.push({
                childId: child.id,
                childName: child.name,
                type: 'journey_progress',
                title: `Progress in ${journey?.name || 'Journey'}`,
                subtitle: `${jp.completed}/${jp.total} lessons`,
                date: progress.last_activity || new Date().toISOString(),
                icon: Route,
                color: 'purple'
              });
            }
          });
        }
      });
      
      // Sort by date (most recent first)
      return activities.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 20);
    };
    
    const activities = getRecentActivity();
    
    const colorClasses = {
      green: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      amber: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
      purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
      cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' }
    };
    
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="glass-panel p-6 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              Activity History
            </h3>
            <button onClick={() => setShowActivityDetail(false)} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="overflow-y-auto flex-1 space-y-3">
            {activities.length > 0 ? (
              activities.map((activity, index) => {
                const IconComponent = activity.icon;
                const colors = colorClasses[activity.color] || colorClasses.cyan;
                
                return (
                  <div key={index} className={`bg-slate-900/50 rounded-xl p-4 border ${colors.border}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className={`w-5 h-5 ${colors.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium">{activity.title}</span>
                        </div>
                        {activity.subtitle && (
                          <p className="text-slate-400 text-sm">{activity.subtitle}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-slate-500 text-xs">{activity.childName}</span>
                          <span className="text-slate-600 text-xs">•</span>
                          <span className="text-slate-500 text-xs">
                            {new Date(activity.date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No activity yet</p>
                <p className="text-slate-500 text-sm mt-1">Activity will appear here as children complete lessons</p>
              </div>
            )}
          </div>
          
          <button
            onClick={() => { setShowActivityDetail(false); navigate('/analytics'); }}
            className="mt-4 w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl px-4 py-3 font-semibold hover:opacity-90 transition-all"
          >
            View Full Analytics
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0F172A] px-6 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 sm:mb-12">
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Parent Avatar */}
            <div className="relative flex-shrink-0">
              {parentAvatar && Object.keys(parentAvatar).length > 0 ? (
                <AvatarPreview avatar={parentAvatar} size="lg" type="guide" />
              ) : (
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-amber-500/20 flex items-center justify-center border-2 border-amber-500/30">
                  <User className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
                </div>
              )}
              <button
                onClick={() => setShowAvatarBuilder(true)}
                className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-all flex items-center gap-1 ${
                  parentAvatar && Object.keys(parentAvatar).length > 0
                    ? 'bg-slate-700/80 hover:bg-slate-600/80 text-slate-200'
                    : 'bg-amber-500/90 hover:bg-amber-400/90 text-white animate-pulse'
                }`}
                data-testid="parent-avatar-btn"
              >
                <Wand2 className="w-2.5 h-2.5" />
                {parentAvatar && Object.keys(parentAvatar).length > 0 ? 'Edit' : 'Create'}
              </button>
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-4xl font-philosopher font-bold text-white glow-text">
                Parent Dashboard
              </h1>
              <p className="text-slate-400 text-sm sm:text-base mt-1 truncate">Welcome back, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-end sm:self-auto">
            {/* Upgrade Button for Free Users */}
            {billingStatus && !billingStatus.has_premium && (
              <button
                onClick={() => navigate('/billing')}
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-medium text-sm transition-all"
                data-testid="upgrade-btn"
              >
                <Crown className="w-4 h-4" />
                <span className="hidden sm:inline">Upgrade</span>
              </button>
            )}
            {/* Premium Badge for Subscribers */}
            {billingStatus?.has_premium && (
              <button
                onClick={() => navigate('/billing')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  billingStatus.founder_lifetime
                    ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                    : 'bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30'
                }`}
                data-testid="premium-badge"
              >
                {billingStatus.founder_lifetime ? (
                  <>
                    <Crown className="w-4 h-4" />
                    <span className="hidden sm:inline">Founder #{billingStatus.founder_number}</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    <span className="hidden sm:inline">Premium</span>
                  </>
                )}
              </button>
            )}
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate('/', { replace: true });
              }}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Stats Cards - Mobile Responsive & Clickable */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
          <button 
            onClick={() => navigate('/child-select')}
            className="glass-panel p-3 sm:p-6 rounded-xl sm:rounded-2xl hover:border-indigo-500/50 transition-all text-left group"
            data-testid="stat-children"
          >
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-500/30 transition-colors">
                <Users className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-400" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-400 text-xs sm:text-sm">Children</p>
                <p className="text-xl sm:text-3xl font-bold text-white">{children.length}</p>
              </div>
            </div>
            <p className="text-xs text-indigo-400/70 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to manage →</p>
          </button>

          <button 
            onClick={() => setShowLessonsDetail(true)}
            className="glass-panel p-3 sm:p-6 rounded-xl sm:rounded-2xl hover:border-green-500/50 transition-all text-left group"
            data-testid="stat-lessons"
          >
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/30 transition-colors">
                <BookOpen className="w-4 h-4 sm:w-6 sm:h-6 text-green-400" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-400 text-xs sm:text-sm">Lessons</p>
                <p className="text-xl sm:text-3xl font-bold text-white">{lessons.filter(l => l.is_published).length}</p>
              </div>
            </div>
            <p className="text-xs text-green-400/70 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View completed →</p>
          </button>

          <button 
            onClick={() => setShowJourneysDetail(true)}
            className="glass-panel p-3 sm:p-6 rounded-xl sm:rounded-2xl hover:border-purple-500/50 transition-all text-left group"
            data-testid="stat-paths"
          >
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/30 transition-colors">
                <Route className="w-4 h-4 sm:w-6 sm:h-6 text-purple-400" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-400 text-xs sm:text-sm">Journeys</p>
                <p className="text-xl sm:text-3xl font-bold text-white">{paths.length}</p>
              </div>
            </div>
            <p className="text-xs text-purple-400/70 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View progress →</p>
          </button>

          <button 
            onClick={() => setShowSparksDetail(true)}
            className="glass-panel p-3 sm:p-6 rounded-xl sm:rounded-2xl hover:border-amber-500/50 transition-all text-left group"
            data-testid="stat-sparks"
          >
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/30 transition-colors">
                <Sparkles className="w-4 h-4 sm:w-6 sm:h-6 text-amber-400" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-400 text-xs sm:text-sm">Total Sparks</p>
                <p className="text-xl sm:text-3xl font-bold text-white">{getTotalSparks()}</p>
              </div>
            </div>
            <p className="text-xs text-amber-400/70 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View details →</p>
          </button>

          <button 
            onClick={() => setShowActivityDetail(true)}
            className="glass-panel p-3 sm:p-6 rounded-xl sm:rounded-2xl hover:border-cyan-500/50 transition-all text-left group"
            data-testid="stat-activity"
          >
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-500/30 transition-colors">
                <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-cyan-400" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-400 text-xs sm:text-sm">Activity</p>
                <p className="text-xl sm:text-3xl font-bold text-white">{children.length > 0 ? 'View' : '—'}</p>
              </div>
            </div>
            <p className="text-xs text-cyan-400/70 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View history →</p>
          </button>
        </div>

        {/* Quick Actions - Mobile Responsive */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
          <button
            data-testid="manage-children-btn"
            onClick={() => navigate('/child-select')}
            className="glass-panel p-6 rounded-2xl hover:border-white/20 transition-all text-left group"
          >
            <Users className="w-8 h-8 text-indigo-400 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-xl font-philosopher font-bold text-white mb-2">Manage Children</h3>
            <p className="text-slate-400 text-sm">Add, edit, or view child profiles</p>
          </button>

          <button
            data-testid="create-lesson-btn"
            onClick={() => navigate('/lesson-builder')}
            className="glass-panel p-6 rounded-2xl hover:border-white/20 transition-all text-left group"
          >
            <PenTool className="w-8 h-8 text-green-400 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-xl font-philosopher font-bold text-white mb-2">Lesson Builder</h3>
            <p className="text-slate-400 text-sm">Create lessons with modular blocks</p>
          </button>

          <button
            data-testid="analytics-btn"
            onClick={() => navigate('/analytics')}
            className="glass-panel p-6 rounded-2xl hover:border-white/20 transition-all text-left group"
          >
            <BarChart3 className="w-8 h-8 text-purple-400 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-xl font-philosopher font-bold text-white mb-2">Analytics</h3>
            <p className="text-slate-400 text-sm">Track progress & engagement</p>
          </button>

          {children.length > 0 && (
            <div
              className="glass-panel p-6 rounded-2xl hover:border-white/20 transition-all text-left"
            >
              <TrendingUp className="w-8 h-8 text-cyan-400 mb-3" />
              <h3 className="text-xl font-philosopher font-bold text-white mb-2">View Progress</h3>
              <p className="text-slate-400 text-sm mb-3">Track your children's learning</p>
              <div className="flex flex-wrap gap-2">
                {children.slice(0, 3).map((child) => (
                  <button
                    key={child.id}
                    data-testid={`view-progress-${child.id}`}
                    onClick={() => navigate(`/child-progress/${child.id}`)}
                    className="px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-full text-xs transition-all"
                  >
                    {child.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Enter Temple - Prominent position for easy access */}
        {children.length > 0 && (
          <div className="mb-8 sm:mb-12 text-center">
            <button
              data-testid="enter-temple-btn-top"
              onClick={() => navigate('/child-select')}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-full px-8 py-4 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/25 font-semibold text-lg flex items-center gap-3 mx-auto"
            >
              <Sparkles className="w-5 h-5" />
              Enter Temple as Child
            </button>
            <p className="text-slate-500 text-sm mt-2">Choose a child profile to explore the Temple</p>
          </div>
        )}

        {/* Pending Community Shares */}
        <PendingSharesPanel />

        {/* Temple Listens Reading Progress */}
        {children.length > 0 && (
          <ReadingProgressPanel children={children} />
        )}

        {/* Recess Settings */}
        {children.length > 0 && (
          <RecessSettingsPanel children={children} />
        )}

        {/* Lessons List - Mobile Responsive */}
        <div className="glass-panel p-4 sm:p-6 rounded-xl sm:rounded-2xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white">Your Lessons</h2>
            {lessons.length === 0 && (
              <button
                data-testid="create-sample-lessons-btn"
                onClick={handleCreateSampleLessons}
                className="bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 border border-indigo-400/30 rounded-full px-4 py-2 text-sm transition-all duration-300 hover:scale-105"
              >
                Create Sample Lessons
              </button>
            )}
          </div>

          {lessons.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-sm sm:text-base">No lessons yet. Create your first lesson!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lessons.map((lesson) => (
                <div
                  key={lesson.id}
                  className="bg-slate-900/50 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-900/70 transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <h3 className="text-base sm:text-lg font-semibold text-white truncate">{lesson.title}</h3>
                      {lesson.is_published && (
                        <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-semibold flex-shrink-0">
                          Published
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400 text-xs sm:text-sm mt-1 truncate">
                      {getRoomName(lesson.room_id)} • {lesson.blocks?.length || 0} blocks
                    </p>
                  </div>
                  <div className="flex gap-2 self-end sm:self-auto flex-shrink-0">
                    <button
                      data-testid={`edit-lesson-${lesson.id}`}
                      onClick={() => navigate(`/lesson-builder?id=${lesson.id}`)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-5 h-5 text-slate-400 hover:text-white" />
                    </button>
                    <button
                      data-testid={`delete-lesson-${lesson.id}`}
                      onClick={() => handleDeleteLesson(lesson.id)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5 text-slate-400 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Child Selection Modal for Journeys */}
      {showChildSelectModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-panel p-6 rounded-2xl max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
                <Route className="w-5 h-5 text-purple-400" />
                Select Explorer
              </h3>
              <button
                onClick={() => setShowChildSelectModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-400 text-sm mb-4">Choose which child's learning journey to view:</p>
            <div className="space-y-3">
              {children.map((child) => (
                <button
                  key={child.id}
                  data-testid={`select-child-journey-${child.id}`}
                  onClick={() => {
                    localStorage.setItem('selectedChild', JSON.stringify(child));
                    setShowChildSelectModal(false);
                    navigate('/journey-selection');
                  }}
                  className="w-full p-4 bg-slate-900/50 hover:bg-purple-500/20 border border-slate-700/50 hover:border-purple-500/50 rounded-xl text-left transition-all flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{child.name}</p>
                    <p className="text-slate-500 text-xs">View their learning journeys</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Parent Avatar Builder Modal */}
      <AvatarBuilderModal
        isOpen={showAvatarBuilder}
        onClose={() => setShowAvatarBuilder(false)}
        childId={null} // null indicates parent/guide avatar
        initialAvatar={parentAvatar}
        onSave={handleAvatarSave}
      />
      
      {/* Drill-Down Detail Modals */}
      {showLessonsDetail && <LessonsDetailModal />}
      {showSparksDetail && <SparksDetailModal />}
      {showJourneysDetail && <JourneysDetailModal />}
      {showActivityDetail && <ActivityDetailModal />}
      
      {/* Beta Feedback Button */}
      <FeedbackButton variant="floating" />
    </div>
  );
};

export default ParentDashboard;
